import {html, css, LitElement, nothing} from 'lit';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {ActionBehavior} from '../behaviors/action-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {OutsideClickBehavior} from '../behaviors/outside-click-behavior.js';

class KeliPanelMenu extends OutsideClickBehavior(HotkeyBehavior(ActionBehavior(ScopeBinding(LitElement)))) {
  static styles = css`
      :host {
          backface-visibility: hidden;
          box-sizing: border-box;
          color: var(--keli-chrome-text-color);
          display: flex;
          flex-direction: column;
          left: 0;
          height: calc(90% - var(--keli-panel-menu-top, 0));
          max-height: calc(90% - var(--keli-panel-menu-top, 0));
          overflow: hidden;
          position: absolute;
          right: 0;
          top: var(--keli-panel-menu-top, 0);
          transform-origin: top;
          transform: scaleY(0);
          transition: transform var(--keli-transition-fast);
          z-index: var(--keli-zindex-panel-menu);
      }

      :host(.open) {
          transform: scaleY(1);
      }

      #searchContainer {
          align-items: center;
          border-bottom: 2px solid var(--keli-panel-menu-border-color);
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          padding: .3em 0;
          white-space: nowrap;
      }

      #searchLabel {
          width: 60px;
          display: inline-block;
          text-align: center;
          font-size: .5em;
          text-transform: uppercase;
          vertical-align: middle;
      }

      #search {
          background-color: var(--keli-panel-menu-search-background-color);
          border-radius: 25px;
          border: none;
          color: var(--keli-chrome-text-color);
          flex: 1;
          font-family: inherit;
          font-size: 1em;
          margin: 0 10px 0 0;
          max-width: 300px;
          outline: none;
          padding: 5px 15px;
      }

      #bodyContainer {
          display: flex;
          flex-grow: 1;
          flex-direction: row;
          position: relative;
      }

      #sections {
          padding: 5px 0;
          text-align: center;
          width: 60px;
          border-right: 2px solid var(--keli-panel-menu-border-color);
      }

      #items {
          column-fill: auto;
          columns: 220px auto;
          flex: 1;
          font-size: 0.85em;
          overflow: hidden auto;
          padding: 5px 0;
      }

      .selected {
          background-color: var(--keli-action-hover-background);
      }

      keli-menu-item {
          cursor: pointer;
          break-inside: avoid;
      }

      keli-menu-item.hidden {
          display: none;
      }
  `;

  static properties = {
    AdjacentElement: {type: Object},
    SectionAdjacentElement: {type: Object},
    items: {type: Map},
    matchizzle: {type: Object},
    opened: {type: Boolean},
    sections: {type: Array},
    selectedItem: {type: Object},
    selectedSection: {type: Object},
    toggleElement: {type: Object},
    visibleItems: {type: Array},
  };

  constructor() {
    super();
    this.sections = [
      {keliname: 'editactions', caption: 'Actions', icon: 'insert-drive-file'},
      {keliname: 'filteractions', caption: 'Filters', icon: 'table-filter'},
      {keliname: 'toolactions', caption: 'Tools', icon: 'tools'},
      {keliname: 'reportactions', caption: 'Reports', icon: 'printer'},
      {keliname: 'relatedactions', caption: 'Related', icon: 'content-duplicate'},
      {keliname: 'developeractions', caption: 'Developer', icon: 'code-braces-box'},
    ];
    this.items = new Map();
  }

  render() {
    return html`
        <div id="searchContainer">
            <span id="searchLabel">Search</span>
            <input type="text" id="search" @input="${this.search}" autocomplete="off">
        </div>

        <div id="bodyContainer">
            <div id="sections">
                ${this.opened ? this.sections.map(item => html`
                    <keli-panel-menu-section
                            @click="${this.activateSection}"
                            keliname="${item.keliname}"
                            icon="${item.icon}"
                            caption="${item.caption}"></keli-panel-menu-section>
                `) : nothing}
            </div>
            <div id="items">
                ${this.opened && this.items?.size ? Array.from(this.items.values(), item => html`
                    <keli-menu-item
                            @click="${this.activateMenuItem}"
                            @mouseenter="${this.selectMenuItem}"
                            enable-update-events
                            @keliMenuItemUpdated="${this.handleKeliMenuItemUpdated}"
                            id="${item.id}"
                            keliname="${item.keliname}"
                            prefix="${item.captionPrefix}"
                            subtext="${item.hotkeyText}"
                            .keliTargetScopeRoot=${item.keliTargetScopeRoot}
                            .dependsOnBrowse=${item.dependsOnBrowse}
                            .browseSettled=${item.browseSettled}></keli-menu-item>`) : nothing}
            </div>
        </div>
    `;
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    for (const [property, value] of changedProperties.entries()) {
      if (this[`${property}Changed`]) {
        this[`${property}Changed`](this[property], value);
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('outside-click', this.handleOutsideClick);

    this.registerHotkey(KeliInterface.Hotkeys.panel.menu.choose, {
      down: (event) => {
        if (this.selectedItem) {
          this.activateMenuItem(null, this.selectedItem.id);
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.panel.menu.close, {
      down: () => {
        this.opened = false;
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.panel.menu.next, {
      down: () => {
        this.AdjacentElement.next(this.selectedItem);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.panel.menu.previous, {
      down: () => {
        this.AdjacentElement.previous(this.selectedItem);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.panel.menu.nextSection, {
      down: () => {
        this.SectionAdjacentElement.next(this.selectedSection);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.panel.menu.previousSection, {
      down: () => {
        this.SectionAdjacentElement.previous(this.selectedSection);
      }
    });
  }

  firstUpdated() {
    this.SectionAdjacentElement = new AdjacentElement({
      'this': this,
      'selectorNode': this.renderRoot.querySelector('#sections'),
      'selector': 'keli-panel-menu-section',
      'properties': {visible: [true, undefined]},
      'wrap': false,
      'defaultCallback': (newElement) => {
        this.selectedSection = newElement;
      }
    });

    this.AdjacentElement = new AdjacentElement({
      'this': this,
      'selectorNode': this.renderRoot.querySelector('#items'),
      'selector': 'keli-menu-item',
      'properties': {hidden: false},
      'wrap': false,
      'defaultCallback': (newElement) => {
        this.selectedItem = newElement;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('outside-click', this.handleOutsideClick);

    this.matchizzle = null;
    if (this.SectionAdjacentElement) {
      this.SectionAdjacentElement.destroy();
      this.SectionAdjacentElement = null;
    }
    if (this.AdjacentElement) {
      this.AdjacentElement.destroy();
      this.AdjacentElement = null;
    }
    this.selectedItem = null;
    this.selectedSection = null;
    this.toggleElement = null;
    this.visibleItems = null;
    this.sections = null;
    this.items = null;
  }

  toggleMenu() {
    if (this.disabled && !this.opened) {
      return;
    }
    this.opened = !this.opened;
  }

  activateSection(event) {
    this.selectedSection = event.target;
    this.focusSearch();
  }

  activateMenuItem(event, itemId) {
    const item = this.items?.get(itemId || event.target?.id);
    if (item) {
      if (item.dependsOnBrowse && !item.browseSettled) {
        return;
      }
      this._activate(event, {
        ...item.extraEventProperties,
        keliname: item.keliname,
        scopeRoot: item.keliTargetScopeRoot
      });
    }

    this.opened = false;
  }

  selectMenuItem(event) {
    this.selectedItem = event.target;
  }

  handleKeliMenuItemUpdated(event) {
    const item = this.items?.get(getEventTarget(event)?.id);
    if (item && event.detail.property === 'caption') {
      item.caption = event.detail.value;
    }
  }

  handleOutsideClick() {
    this.opened = false;
  }

  selectedItemChanged(newValue, oldValue) {
    if (oldValue) {
      oldValue.classList.remove('selected');
    }
    if (newValue) {
      newValue.classList.add('selected');
      newValue.scrollIntoView({block: 'nearest'});
    }
  }

  selectedSectionChanged(newValue, oldValue) {
    if (oldValue) {
      oldValue.classList.remove('selected');
    }
    if (newValue) {
      newValue.classList.add('selected');
      this.renderRoot.querySelector('#search').value = '';
      this.search(null, newValue);
    }
  }

  openedChanged(newValue) {
    this.resetHotkeyState();
    this.classList.toggle('open', newValue);
    this.SectionAdjacentElement && this.SectionAdjacentElement.first();

    if (newValue) {
      this.focusSearch();
      this.dispatchEvent(new CustomEvent('globalFocusGrabbed', {bubbles: true, composed: true}));
      this.outsideClickElements = [this.toggleElement];
      this.outsideClickListening = true;
    } else {
      this.renderRoot.querySelector('#search').value = '';
      this.dispatchEvent(new CustomEvent('globalFocusReleased', {bubbles: true, composed: true}));
      this.outsideClickListening = false;
    }
  }

  visibleItemsChanged(newValue) {
    const elements = this.renderRoot.querySelectorAll('keli-menu-item');
    for (const element of elements) {
      if (newValue?.some?.(item => item.id === element.id)) {
        element.hidden = false;
        element.classList.remove('hidden');
      } else {
        element.hidden = true;
        element.classList.add('hidden');
      }
    }
    this.AdjacentElement?.first();
  }

  focusSearch() {
    if (!KeliInterface.isTouchDevice) {
      this.renderRoot.querySelector('#search').focus();
    }
  }

  search(event, section) {
    this.selectedItem = null;
    this.selectedSection = section || null;
    const searchValue = this.renderRoot.querySelector('#search').value;

    if (!searchValue && !section) {
      this.SectionAdjacentElement?.first();
      return;
    }

    // This has to be done every time because captions can be dynamic
    if (!this.matchizzle) {
      this.matchizzle = new Matchizzle([], {keys: ['text'], id: 'element'});
    }
    this.matchizzle.list = [];

    if (!this.items?.size) {
      return;
    }
    for (let item of this.items.values()) {
      if (section && section.keliname !== item.menuSection) {
        continue;
      }
      if (item.registerToElementIsHidden) { // TODO: Figure this out.
        continue;
      }
      this.matchizzle.list.push({
        element: item,
        text: `${item.captionPrefix || ''}: ${item.caption || ''}`
      });
    }

    this.visibleItems = this.matchizzle?.search(searchValue);
  }

  addItemHotkey(item, hotkey) {
    if (!item || !hotkey) {
      return;
    }
    if (item.registerToElement) {
      item.registerToElement.registerHotkey({
        'keys': hotkey.toLowerCase(),
        'is_unordered': true,
        'prevent_repeat': true
      }, {
        down: async (event) => {
          if (item.registerToElement._keliAccept) {
            await item.registerToElement._keliAccept();
          }
          this.activateMenuItem(null, item.id);
        }
      });
    } else {
      this.dispatchEvent(new CustomEvent('registerPanelHotkey', {
        bubbles: true,
        composed: true,
        detail: {
          'keys': hotkey, 'scope': this, action: () => {
            this.activateMenuItem(null, item.id);
          }
        }
      }));
    }
  }

  removeItemHotkey(item, hotkey) {
    if (!item || !hotkey) {
      return;
    }
    if (item.registerToElement) {
      item.registerToElement.unregisterHotkey?.({'keys': hotkey.toLowerCase()});
    } else {
      this.dispatchEvent(new CustomEvent('unregisterPanelHotkey', {
        bubbles: true,
        composed: true,
        detail: {'keys': hotkey}
      }));
    }
  }

  updateMenuItems(definition) {
    for (const item of definition) {
      if (item.visible === false) {
        if (item.hotkeys?.length) {
          item.hotkeys.forEach(hotkey => this.removeItemHotkey(item, hotkey));
        }
        this.items?.delete(item.id);
        continue;
      }

      const existing = this.items?.get(item.id);
      if (existing) {
        Object.assign(existing, item);
      } else {
        this.items?.set(item.id, item);
        if (item.hotkeys?.length) {
          item.hotkeyText = formatHotkeyAsText(item.hotkeys);
          item.hotkeys.forEach(hotkey => this.addItemHotkey(item, hotkey));
        }
      }
    }
    this.requestUpdate();
    this.search(null, this.selectedSection);
  }

  updateBrowseSettled(dependsOnBrowse, newValue) {
    if (!this.items?.size) {
      return;
    }
    const updates = [];
    for (const item of this.items.values()) {
      if (item.dependsOnBrowse === dependsOnBrowse) {
        updates.push({id: item.id, browseSettled: newValue});
      }
    }
    if (updates.length) {
      this.updateMenuItems(updates);
    }
  }

  toggleRelatedItems(parentElement, active) {
    if (!this.items?.size) {
      return;
    }
    for (const item of this.items.values()) {
      if (item.registerToElement === parentElement) {
        item.registerToElementIsHidden = !active;
      }
    }
    this.search(null, this.selectedSection);
  }
}

customElements.define('keli-panel-menu', KeliPanelMenu);